/**
 * AdminFunnel — /admin/funnel
 *
 * The marketplace bridge debugging tool. 6 fixed steps, raw counts +
 * conversion %, biggest-drop highlight. No charts, no filters, no
 * cohorts — just the leak-finder.
 *
 *   Visitors → Uploads → CTA clicks → Supplier clicks → Messages → Bookings
 *
 * "You're in 0 → 1 growth mode now. Measurement > Features."
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Warning, CaretRight } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RANGES = [
  { key: '24h', label: '24h' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
];

export default function AdminFunnel() {
  const navigate = useNavigate();
  const [range, setRange] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (r) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/funnel`, {
        params: { range: r, include: 'top_events' },
      });
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 403) setError('Admin access required');
      else if (e.response?.status === 401) {
        toast.error('Please log in');
        navigate('/login');
      } else setError('Could not load funnel');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(range); }, [load, range]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0F1014] flex items-center justify-center text-white" data-testid="funnel-error">
        <div className="text-center px-6">
          <Warning size={32} weight="fill" className="text-amber-300 mx-auto mb-3" />
          <p className="font-black">{error}</p>
        </div>
      </div>
    );
  }

  const steps = data?.steps || [];
  const biggest = data?.biggest_drop;
  const topEvents = data?.top_events || [];

  return (
    <div className="min-h-screen bg-[#0F1014] text-white pb-12">
      {/* Header */}
      <div className="border-b border-white/8 bg-[#0F1014]/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate('/admin/growth')}
            className="text-white/60 hover:text-white text-xs font-bold flex items-center gap-1.5"
            data-testid="back-btn"
          >
            <ArrowLeft size={14} weight="bold" /> Growth
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-black truncate" data-testid="page-title">Marketplace funnel</h1>
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-0.5">
              Where the bridge leaks · Last {range}
            </p>
          </div>
          <div className="flex gap-1 rounded-full bg-white/5 p-1" data-testid="range-toggle">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={`px-3 py-1 rounded-full text-[11px] font-black transition-colors ${
                  range === r.key ? 'bg-white text-[#0F1014]' : 'text-white/60 hover:text-white'
                }`}
                data-testid={`range-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-8 space-y-6">
        {loading && (
          <div className="text-center text-white/40 text-sm py-12" data-testid="funnel-loading">
            Loading…
          </div>
        )}

        {!loading && (
          <>
            {/* Biggest drop callout */}
            {biggest && (
              <div
                className="rounded-2xl px-4 py-3 flex items-center gap-3 bg-amber-400/10 border border-amber-400/30 text-amber-100"
                data-testid="biggest-drop-callout"
              >
                <Warning size={20} weight="fill" className="shrink-0 text-amber-300" />
                <div className="flex-1">
                  <p className="font-black text-sm">
                    Biggest drop: {biggest.from_label} → {biggest.to_label}
                  </p>
                  <p className="text-[11px] text-amber-100/70">
                    Only {biggest.conversion_pct}% pass through this step. Fix this first.
                  </p>
                </div>
              </div>
            )}

            {/* The funnel itself */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-1" data-testid="funnel-strip">
              {steps.map((s, i) => (
                <FunnelRow
                  key={s.key}
                  step={s}
                  isFirst={i === 0}
                  isLast={i === steps.length - 1}
                  isWorst={biggest?.to === s.key}
                />
              ))}
            </div>

            {/* Empty-state hint */}
            {steps.every((s) => s.value === 0) && (
              <div className="text-center text-white/40 text-xs py-6" data-testid="funnel-empty">
                No funnel events recorded in this window yet.
              </div>
            )}

            {/* Top performing events — what's already working */}
            {topEvents.length > 0 && (
              <section data-testid="top-events-section" className="space-y-2">
                <div className="flex items-baseline justify-between px-1">
                  <h2 className="text-sm font-black flex items-center gap-1.5">
                    <span className="text-orange-300">🔥</span> Top converting events
                  </h2>
                  <p className="text-[10px] uppercase tracking-[0.16em] text-white/35">
                    Min 30 visits · Sorted by booking rate
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
                  {topEvents.map((e, i) => (
                    <TopEventRow key={e.event_id} ev={e} rank={i + 1} />
                  ))}
                </div>
              </section>
            )}

            {/* Insight footer */}
            <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4 text-[11px] text-white/55 leading-relaxed">
              <p className="font-black text-white/70 text-xs mb-1.5">How to read this</p>
              <ul className="space-y-1">
                <li>• Upload but no CTA click → CTA copy / placement weak</li>
                <li>• CTA click but no supplier click → directory cards aren't compelling</li>
                <li>• Supplier click but no message → profile / pricing is friction</li>
                <li>• Message but no booking → trust or quote-flow issue</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FunnelRow({ step, isFirst, isLast, isWorst }) {
  const pct = step.conversion_pct;
  const tone = isLast ? 'emerald' : isWorst ? 'amber' : 'cyan';
  const valueClass =
    tone === 'emerald'
      ? 'text-emerald-300'
      : tone === 'amber'
      ? 'text-amber-300'
      : 'text-[#C29D52]';

  return (
    <div
      className="flex items-center gap-3 py-3 border-b border-white/5 last:border-0"
      data-testid={`funnel-row-${step.key}`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.18em] text-white/40">{step.label}</p>
        <p className={`text-2xl font-black ${valueClass}`} data-testid={`funnel-value-${step.key}`}>
          {step.value.toLocaleString()}
        </p>
      </div>
      {!isFirst && (
        <div className="flex items-center gap-2 text-right" data-testid={`funnel-rate-${step.key}`}>
          <CaretRight size={12} weight="bold" className="text-white/30" />
          <div>
            <p className={`text-sm font-black ${isWorst ? 'text-amber-300' : 'text-white'}`}>
              {pct === null ? '—' : `${pct}%`}
            </p>
            <p className="text-[9px] uppercase tracking-[0.16em] text-white/35">conv</p>
          </div>
        </div>
      )}
    </div>
  );
}

function TopEventRow({ ev, rank }) {
  const msgPct = (ev.message_rate * 100).toFixed(1);
  const bookPct = (ev.booking_rate * 100).toFixed(1);
  const isTop = rank === 1;
  return (
    <div
      className="px-4 py-3 flex items-center gap-3"
      data-testid={`top-event-row-${ev.event_id}`}
    >
      <span
        className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black ${
          isTop ? 'bg-orange-300/20 text-orange-200' : 'bg-white/[0.06] text-white/55'
        }`}
      >
        {rank}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-black text-white truncate" data-testid={`top-event-name-${ev.event_id}`}>
          {ev.event_name}
        </p>
        <p className="text-[11px] text-white/50 mt-0.5">
          {ev.visits.toLocaleString()} visits → {ev.messages.toLocaleString()} messages → {ev.bookings.toLocaleString()} bookings
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-[11px] font-black text-[#C29D52]" data-testid={`top-event-msg-rate-${ev.event_id}`}>
          {msgPct}% msg
        </p>
        <p className="text-[11px] font-black text-emerald-300" data-testid={`top-event-book-rate-${ev.event_id}`}>
          {bookPct}% book
        </p>
      </div>
    </div>
  );
}
